import {useContext} from 'react';

import {Alert, AlertTitle, Backdrop, Box, DialogActions, useMediaQuery, useTheme} from '@mui/material';
import {useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';

import BookingFormContext from './BookingFormContext';
import {EventInfoContainer} from './Style';

import {patchVotingPoll} from 'Api/Calendar';
import ApptsContext from 'Components/Appointments/ApptsContext';
import EventInfo from 'Components/EventInfo';
import {ZmPrimaryBtn, ZmSecondaryBtn} from 'Components/ZmButtons';
import ZmSpinner from 'Components/ZmSpinner';
import {CaptchaError} from 'Error/CaptchaError';
import {HttpError} from 'Error/HttpError';
import {NWS_ERROR_CODE} from 'Store/IntegrationStore';
import {isAnonymousTeamAppt} from 'Utils/apptAvailabilityUtils';
import {useQueryWithCaptchaPasscode} from 'Utils/Captcha';


/**
 * Booking Form Component -
 *   Renders the form to intake the user's name and email and manages form submission
 * @param {Object} props
 * @param {Object} props.appointment the top level appt object, used to display information
 * @param {string} props.calendarId
 * @param {function} props.onBook callback on successful book response
 * @param {function} props.onCancel callback on dialog close
 * @param {Date} props.startDateTime slot start time to book
 * @return {ReactElement}
 */
export function RescheduleVotingForm(props) {
  const {
    appointment,
    calendarId,
    onBook,
    onCancel,
    startDateTime,
    timeZone,
  } = props;
  const {
    displayOtp,
    errorState,
    isSubmitting,
    setErrorState,
    setIsSubmitting,
  } = useContext(BookingFormContext);

  const {
    isBookerVoting,
    voterInfo,
    votes,
  } = useContext(ApptsContext);

  const {
    handleSubmit,
  } = useFormContext();
  const {t} = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const createrDisplayName = useSelector((s) => s.bookAppointments.createrDisplayName);

  const doVote = useQueryWithCaptchaPasscode(async (calendarId, recaptchaToken) => {
    const votesArr = Object.values(votes);
    const spots = votesArr.map((vote) => {
      return {
        start: vote.start.toISOString(),
      };
    });

    return await patchVotingPoll(calendarId, voterInfo.pendingEventId, {
      voter: {
        email: voterInfo.email,
        name: voterInfo.name,
        id: voterInfo?.voterId || '',
      },
      spots: spots,
      timeZone: timeZone,
      recaptchaToken,
    });
  });

  const handleBook = async () => {
    try {
      const bookedEvent = await doVote(calendarId);
      console.log('Booked appt', bookedEvent);
      onBook(bookedEvent, voterInfo.email);
    } catch (e) {
      console.error('Failed to book:', e, e.errorCode, e.message);
      let reason = '';
      if (e instanceof CaptchaError) {
        reason = t('booking.captchaPrompt');
      } else if (e instanceof HttpError) {
        switch (e.errorCode) {
          case 400:
            if (e.body?.error?.message && e.body?.error.message.includes('verification code')) {
              reason = t('booking.incorrectPasscode');
            } else {
              reason = e.body?.error?.message || t('booking.genericHttpValidationError');
            }
            break;
          case 401:
            reason = e.body?.error?.message || 'Unauthorized';
            break;
          case 403:
            reason = t('booking.incorrectPasscode');
            window.sessionStorage.removeItem('code');
            break;
          case 404:
            reason = t('error.errorMsgBody404');
            break;
          case 500:
            reason = t('error.errorMsgBody500');
            if ([NWS_ERROR_CODE.INVALID_GRANT,
              'Invalid Credentials', 'connection is empty'].some((item) => e.message?.includes(item))) {
              reason = t('booking.hostAccountInvalid');
            }
            if (e?.body?.error?.message?.includes('Permission deny')) {
              reason = t('booking.hostAccountInvalid');
            }
            break;
          default:
            reason = t('booking.genericHttpError');
            break;
        }
      } else {
        reason = t('booking.genericError');
      }
      setErrorState({
        isError: true,
        reason: reason,
      });
    }
  };

  const clearOnCancel = () => {
    setErrorState({
      isError: false,
      reason: '',
    });
  };

  const onSubmit = () => {
    setIsSubmitting(true);
    handleSubmit(async () => {
      if (appointment.verify && !displayOtp) {
        clearOnCancel();
        setIsSubmitting(false);
      } else {
        try {
          await handleBook();
          // after booked success, current verification code is not valid anymore
          window.sessionStorage.removeItem('verifySentTime');
        } catch (e) {
          console.error('Encounter unexpected error when booking appointment:', e);
        } finally {
          setIsSubmitting(false);
        }
      }
    }, () => setIsSubmitting(false))();
  };

  return (
    <>
      <Backdrop
        sx={{
          position: 'absolute',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
        }}
        open={isSubmitting}
      >
        <ZmSpinner size="80" absolute />
      </Backdrop>

      <>
        <Box>
          {errorState.isError &&
              <Alert severity="error" sx={{marginBottom: '16px', borderRadius: '10px'}}>
                <AlertTitle>{t('booking.errorTitle')}</AlertTitle>
                {errorState.reason}
              </Alert>
          }

          <EventInfoContainer>
            <EventInfo
              appointment={appointment}
              displayName={createrDisplayName}
              showAvatar={!isAnonymousTeamAppt(appointment)}
              startDateTime={startDateTime}
              timeZone={timeZone}
              isBookerVoting={isBookerVoting}
              votes={votes}
            />
          </EventInfoContainer>

        </Box>

        <DialogActions sx={{
          marginY: '28px',
          paddingX: '0px',
        }}>
          {!isSmallScreen && <ZmSecondaryBtn
            size="small"
            onClick={() => {
              onCancel();
              clearOnCancel();
            }}
            disabled={isSubmitting}
          >
            {t('common.back')}
          </ZmSecondaryBtn>}
          <ZmPrimaryBtn
            size="small"
            onClick={onSubmit}
            disabled={isSubmitting}
            style={isSmallScreen ? {
              width: '100%',
            }: {}}
          >
            {t('polls.vote')}
          </ZmPrimaryBtn>
        </DialogActions>
      </>
    </>
  );
};
